import { Alert, Button, classnames, SkeletonLine, Title } from '@terveystalo/design-system-react-components';
import { ApiModelType } from 'model/api';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressBar } from 'ui/questionary/ProgressBar';
import { useApiData } from 'utils/api/helpers';
import { useAuthContext } from 'utils/auth/auth-context';
import { isEmbeddedToPRSP } from 'utils/communication/config';
import { sendAppError, sendMessage } from 'utils/communication/useSendMessages';
import { useUiContext } from 'utils/react/ui-context';
import { ownApiAnswer } from 'utils/types/common';

type Props = {
  onClickNext: () => void;
  session: string;
  answers: ownApiAnswer[];
  handleSecureMetaDataUpdate: (secureMetaData: ApiModelType['ChatMetaDataResponse']) => void;
  handleEncryptedMetaDataUpdate: (encryptedMetaData: ApiModelType['EncryptedMetaDataResponse']) => void;
};

const boldedLeadTextStyle = classnames('mr-0.5', 'text-style-subtitle-xs');

export const SummaryPage: FC<Props> = ({
  onClickNext,
  session,
  answers,
  handleSecureMetaDataUpdate,
  handleEncryptedMetaDataUpdate,
}) => {
  const { t } = useTranslation();
  const { authState } = useAuthContext();
  const { config } = useUiContext();
  const [ResultData] = useApiData('getResult', [
    authState.type === 'LoggedIn' && authState.accessToken ? authState.accessToken : '',
    session,
    answers,
    window.terveystaloApp ? 'TT-App' : 'TT-Online',
    config.CHAT_QUEUE_ID,
  ]);
  /* 
    Fetching both results is only MVP solution and should be changed to select which one to get.
  */
  const [EncryptedResultData] = useApiData('getEncryptedResult', [
    authState.type === 'LoggedIn' && authState.accessToken ? authState.accessToken : '',
    session,
    answers,
  ]);

  /* 
    Please note that logic for sending error messages needs to be revisited when integrating more parents. This is MVP solution.
  */
  useEffect(() => {
    if (ResultData.status === 'REMOTE_DATA_SUCCESS' && !isEmbeddedToPRSP) {
      handleSecureMetaDataUpdate({ secureMetadata: ResultData.data.secureMetaData ?? undefined });
    }
    if (ResultData.status === 'REMOTE_DATA_FAILURE' && !isEmbeddedToPRSP) {
      sendAppError(`Apidata failed: ${ResultData.error}`);
      sendMessage({
        type: 'Error',
        payload: { type: 'InternalError', message: `Apidata failed: ${ResultData.error}` },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ResultData]);

  useEffect(() => {
    if (EncryptedResultData.status === 'REMOTE_DATA_SUCCESS' && isEmbeddedToPRSP) {
      handleEncryptedMetaDataUpdate({ encryptedMetadata: EncryptedResultData.data.encryptedMetaData ?? undefined });
    }
    if (EncryptedResultData.status === 'REMOTE_DATA_FAILURE' && isEmbeddedToPRSP) {
      sendMessage({
        type: 'Error',
        payload: { type: 'InternalError', message: `Apidata failed ${EncryptedResultData.status}` },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [EncryptedResultData]);

  return (
    <div className={classnames('mb-2')}>
      <ProgressBar currentStep={3} />
      <Title as="h1" size="xl" weight="normal" className={classnames('mb-1')}>
        {t('summary')}
      </Title>
      {ResultData.status === 'REMOTE_DATA_LOADING' && (
        <div className={classnames('mb-2.5')}>
          <p className={classnames('mb-0.5')}>
            <SkeletonLine width="60px" className={classnames('my-0.25')} />
          </p>
          <p className={classnames('mb-2')}>
            <SkeletonLine width="60px" className={classnames('my-0.25')} />
          </p>
          <p className={classnames('mb-0.5')}>
            <SkeletonLine width="120px" className={classnames('my-0.5')} />
          </p>
          <p className={classnames('mb-2')}>
            <SkeletonLine width="120px" className={classnames('my-0.5')} />
          </p>
          <p className={classnames('mb-0.5')}>
            <SkeletonLine width="120px" className={classnames('my-0.5')} />
          </p>
          <p>
            <SkeletonLine width="120px" className={classnames('my-0.5')} />
          </p>
        </div>
      )}
      {ResultData.status === 'REMOTE_DATA_SUCCESS' && (
        <>
          <div className={classnames('mb-1.5')}>
            <p>
              <b className={boldedLeadTextStyle}>{`${t('patient')}:`}</b>
              {ResultData.data.patient}
            </p>
            <p>
              <b className={boldedLeadTextStyle}>{`${t('reason_of_contacting')}:`}</b>
              {ResultData.data.reason}
            </p>
          </div>
          {ResultData.data.symptoms?.map(s => (
            <div key={s.title} className={classnames('mb-1.5')}>
              <p>
                <b className={boldedLeadTextStyle}>{s.title} </b>
                {s.text}
              </p>
            </div>
          ))}
        </>
      )}
      {EncryptedResultData.status === 'REMOTE_DATA_SUCCESS' && isEmbeddedToPRSP && (
        <>
          <div className={classnames('mb-1.5')}>
            <p>
              <b className={boldedLeadTextStyle}>{`${t('patient')}:`}</b>
              {EncryptedResultData.data.patient}
            </p>
            <p>
              <b className={boldedLeadTextStyle}>{`${t('reason_of_contacting')}:`}</b>
              {EncryptedResultData.data.reason}
            </p>
          </div>
          {EncryptedResultData.data.symptoms?.map(s => (
            <div key={s.title} className={classnames('mb-1.5')}>
              <p>
                <b className={boldedLeadTextStyle}>{s.title} </b>
                {s.text}
              </p>
            </div>
          ))}
        </>
      )}

      <Alert className="mb-1.5" icon="Info">
        {t('your_answers_are_send_to_specialist')}
      </Alert>
      <Button
        id="to-chat-button"
        as="button"
        type="button"
        icon="ArrowRight"
        onClick={onClickNext}
        className={classnames('mb-2')}
      >
        {t('continue_to_chat')}
      </Button>
    </div>
  );
};
